import type { TtqSession } from '../../services/ttqService';
import { resolveTtqParticleTitle } from '../../utils/ttqDisplay';
import { resolveKnowledgeParticleQaDisplay } from '../../components/KnowledgeParticleQaSections';
import {
  buildDefaultLayers,
  buildDefaultObjectsForLayer,
  normalizeTtqibState,
} from './ttqibStateMigrate';
import {
  reflowAllObjects,
  reflowLayerObjects,
  detectParticleOverlap,
} from './ttqibObjectLayout';
import {
  applyDerivedStackAndVisibility,
  type TtqDerivedFocus,
} from './ttqibDerivedStack';
import { applyAssociationLaneLayout } from './ttqibAssociationLayout';
import type {
  TtqibCanvasObject,
  TtqibCanvasObjectView,
  TtqibNativeCanvasModel,
  TtqibState,
  TtqibViewport,
} from './types';
import { imageObjectId, wholeObjectId } from './types';

function layoutCanvasObjects(
  session: TtqSession,
  objects: TtqibCanvasObject[],
  state: TtqibState,
  derivedFocus?: TtqDerivedFocus | null,
): TtqibCanvasObject[] {
  const stacked = applyDerivedStackAndVisibility(session, objects, derivedFocus);
  // 关联泳道不可挪走 pathRun 衍生粒（否则会从源列下方被拉到右侧）
  const derivedIds = new Set<string>();
  for (const run of session.pathRuns ?? []) {
    if (run.status !== 'done') continue;
    for (const raw of run.derivedParticleIds ?? []) {
      const id = String(raw || '').trim();
      if (id) derivedIds.add(id);
    }
  }
  const associations = (state.associations ?? []).filter((edge) => (
    !derivedIds.has(String(edge.linkedParticleId || '').trim())
    && !derivedIds.has(String(edge.sourceParticleId || '').trim())
  ));
  return applyAssociationLaneLayout(stacked, associations);
}

function resolveObjectText(
  session: TtqSession,
  object: TtqibCanvasObject,
): string {
  const entry = session.particles?.find((item) => item.particleId === object.particleId);
  if (!entry) return '';

  const display = resolveKnowledgeParticleQaDisplay({
    questionSource: entry.particleQuestions,
    answerSource: null,
  }).questionDisplay;

  if (object.type === 'particle-whole') {
    return display.main || resolveTtqParticleTitle(entry, 0);
  }
  if (object.type === 'text-segment') {
    const index = object.segmentIndex ?? 0;
    return display.items[index] ?? '';
  }
  return '';
}

function enrichObjects(
  session: TtqSession,
  objects: TtqibCanvasObject[],
): TtqibCanvasObjectView[] {
  return objects
    // 整卡永不因历史 hidden 丢弃；段/图仍可隐
    .filter((object) => object.type === 'particle-whole' || !object.hidden)
    .map((object) => {
      const entry = session.particles?.find((item) => item.particleId === object.particleId);
      const index = session.particles?.findIndex((item) => item.particleId === object.particleId) ?? 0;
      return {
        ...object,
        hidden: object.type === 'particle-whole' ? false : object.hidden,
        text: resolveObjectText(session, object),
        particleEntry: entry,
        particleTitle: entry ? resolveTtqParticleTitle(entry, Math.max(index, 0)) : object.particleId,
      };
    });
}

export function buildTtqibNativeCanvasModel(
  session: TtqSession,
  savedState?: Partial<TtqibState> | null,
  derivedFocus?: TtqDerivedFocus | null,
): TtqibNativeCanvasModel {
  const state = normalizeTtqibState(session, savedState);
  const stacked = layoutCanvasObjects(session, state.objects, state, derivedFocus);
  const layers = state.layers;
  const activeLayerId = state.activeLayerId ?? layers[0]?.id;
  const objects = enrichObjects(session, stacked);

  return {
    state: { ...state, objects: stacked, activeLayerId },
    layers,
    objects,
    edges: state.edges,
  };
}

/** 按焦点重排/显隐衍生颗粒，并应用关联泳道 */
export function applyDerivedFocusState(
  session: TtqSession,
  state: TtqibState,
  derivedFocus?: TtqDerivedFocus | null,
): TtqibState {
  return {
    ...state,
    objects: layoutCanvasObjects(session, state.objects, state, derivedFocus),
    updatedAt: new Date().toISOString(),
  };
}

export function syncSessionLayers(
  session: TtqSession,
  state: TtqibState,
): TtqibState {
  const nextLayers = buildDefaultLayers(session);
  const layerById = new Map(state.layers.map((layer) => [layer.id, layer]));
  // 会话粒子列表以服务端为准；saved 只能盖展示偏好，不能锁死 sourceParticleIds
  const mergedLayers = nextLayers.map((layer, index) => {
    const saved = layerById.get(layer.id);
    if (!saved) return { ...layer, z: index };
    return {
      ...saved,
      id: layer.id,
      label: layer.label,
      sourceParticleIds: layer.sourceParticleIds,
      z: index,
    };
  });

  const knownLayerIds = new Set(mergedLayers.map((layer) => layer.id));
  const extraLayers = state.layers.filter((layer) => !knownLayerIds.has(layer.id));
  const layers = [...mergedLayers, ...extraLayers.map((layer, offset) => ({ ...layer, z: mergedLayers.length + offset }))];

  const objectById = new Map(state.objects.map((object) => [object.id, object]));
  const nextObjects: TtqibCanvasObject[] = [];

  for (const layer of layers) {
    const existingInLayer = state.objects.filter((object) => object.layerId === layer.id);
    const particleIds = new Set(layer.sourceParticleIds);
    const kept = existingInLayer.filter((object) => particleIds.has(object.particleId));
    const keptParticleIds = new Set(kept.map((object) => object.particleId));

    nextObjects.push(...kept);

    for (const particleId of layer.sourceParticleIds) {
      if (keptParticleIds.has(particleId)) continue;
      const defaults = buildDefaultObjectsForLayer(session, layer, undefined, state.objects);
      nextObjects.push(...defaults.filter((object) => object.particleId === particleId));
    }
  }

  const deduped = new Map<string, TtqibCanvasObject>();
  for (const object of nextObjects) {
    deduped.set(object.id, objectById.get(object.id) ?? object);
  }

  const activeLayerId = state.activeLayerId && layers.some((layer) => layer.id === state.activeLayerId)
    ? state.activeLayerId
    : layers[0]?.id;

  return {
    ...state,
    layers,
    objects: reflowAllObjects(session, layers, [...deduped.values()]),
    activeLayerId,
    layoutRevision: 6,
    updatedAt: new Date().toISOString(),
  };
}

export function updateObjectPosition(
  state: TtqibState,
  objectId: string,
  position: { x: number; y: number },
): TtqibState {
  const target = state.objects.find((object) => object.id === objectId);
  if (!target) return state;

  // 拖整卡：同 particle 的段/图一起平移
  const particleId = target.particleId;
  const origin = target.type === 'particle-whole'
    ? { x: target.x, y: target.y }
    : (() => {
      const whole = state.objects.find((o) => o.id === wholeObjectId(particleId));
      return { x: whole?.x ?? target.x, y: whole?.y ?? target.y };
    })();
  const dx = position.x - origin.x;
  const dy = position.y - origin.y;

  return {
    ...state,
    objects: state.objects.map((object) => {
      if (object.particleId !== particleId) return object;
      if (object.type === 'particle-whole') {
        return { ...object, x: position.x, y: position.y };
      }
      return { ...object, x: object.x + dx, y: object.y + dy };
    }),
    updatedAt: new Date().toISOString(),
  };
}

export function moveParticle(
  state: TtqibState,
  particleId: string,
  position: { x: number; y: number },
  session?: TtqSession | null,
  derivedFocus?: TtqDerivedFocus | null,
): TtqibState {
  const moved = updateObjectPosition(state, wholeObjectId(particleId), position);
  if (!session) return moved;
  return applyDerivedFocusState(session, moved, derivedFocus);
}

/** 量测整卡高度后按衍生纵列重排（展开答案时联动） */
export function measureParticleCard(
  state: TtqibState,
  particleId: string,
  size: { width: number; height: number },
  session: TtqSession,
  derivedFocus?: TtqDerivedFocus | null,
): TtqibState {
  const objectId = wholeObjectId(particleId);
  const target = state.objects.find((object) => object.id === objectId);
  if (!target || target.type !== 'particle-whole') return state;

  const prevHeight = target.height ?? 0;
  const prevWidth = target.width ?? 0;
  if (Math.abs(prevHeight - size.height) < 4 && Math.abs(prevWidth - size.width) < 4) {
    return state;
  }

  const withSize = state.objects.map((object) => (
    object.id === objectId
      ? { ...object, width: size.width, height: size.height }
      : object
  ));

  return applyDerivedFocusState(
    session,
    {
      ...state,
      objects: withSize,
      updatedAt: new Date().toISOString(),
    },
    derivedFocus,
  );
}

export function updateObjectSize(
  state: TtqibState,
  objectId: string,
  size: { width: number; height: number },
  session: TtqSession,
): TtqibState {
  const target = state.objects.find((object) => object.id === objectId);
  if (!target) return state;

  const prevHeight = target.height ?? 0;
  if (Math.abs(prevHeight - size.height) < 8 && Math.abs((target.width ?? 0) - size.width) < 8) {
    return state;
  }

  const withSize = state.objects.map((object) => (
    object.id === objectId ? { ...object, width: size.width, height: size.height } : object
  ));

  const layer = state.layers.find((item) => item.id === target.layerId);
  const reflowed = layer
    ? reflowLayerObjects(session, layer, withSize)
    : withSize;

  return {
    ...state,
    objects: reflowed,
    layoutRevision: 6,
    updatedAt: new Date().toISOString(),
  };
}

export function reflowStateIfNeeded(
  session: TtqSession,
  state: TtqibState,
): TtqibState {
  if ((state.layoutRevision ?? 0) >= 6 && !detectParticleOverlap(state.objects)) {
    return state;
  }
  return {
    ...state,
    objects: reflowAllObjects(session, state.layers, state.objects),
    layoutRevision: 6,
    updatedAt: new Date().toISOString(),
  };
}

export function updateViewport(
  state: TtqibState,
  viewport: TtqibViewport,
): TtqibState {
  return {
    ...state,
    viewport,
    viewportInitialized: true,
    updatedAt: new Date().toISOString(),
  };
}

export function setActiveLayer(
  state: TtqibState,
  layerId: string,
): TtqibState {
  return {
    ...state,
    activeLayerId: layerId,
    updatedAt: new Date().toISOString(),
  };
}

export function mintSegmentOnSelect(
  state: TtqibState,
  objectId: string,
): TtqibState {
  const existing = state.objects.find((object) => object.id === objectId);
  if (existing?.type === 'text-segment') {
    if (existing.segmentId) return state;
    const index = existing.segmentIndex ?? 0;
    const isAnswer = objectId.startsWith('obj:aseg:');
    return {
      ...state,
      objects: state.objects.map((object) => (
        object.id === objectId
          ? {
            ...object,
            segmentId: isAnswer
              ? `aseg:${object.particleId}:${index}`
              : `seg:${object.particleId}:${index}`,
          }
          : object
      )),
      updatedAt: new Date().toISOString(),
    };
  }

  // 点击内嵌段但状态里尚未有记录：从 objectId 解析并写入
  const qMatch = /^obj:seg:([^:]+):(\d+)$/.exec(objectId);
  const aMatch = /^obj:aseg:([^:]+):(\d+)$/.exec(objectId);
  const match = qMatch ?? aMatch;
  if (!match) return state;
  const particleId = match[1];
  const segmentIndex = Number(match[2]);
  const isAnswer = Boolean(aMatch);
  const whole = state.objects.find((object) => object.id === wholeObjectId(particleId));
  if (!whole) return state;

  return {
    ...state,
    objects: [
      ...state.objects,
      {
        id: objectId,
        type: 'text-segment',
        layerId: whole.layerId,
        particleId,
        segmentIndex,
        segmentId: isAnswer
          ? `aseg:${particleId}:${segmentIndex}`
          : `seg:${particleId}:${segmentIndex}`,
        x: whole.x,
        y: whole.y,
        width: whole.width,
      },
    ],
    updatedAt: new Date().toISOString(),
  };
}

export function readParticleIdsFromSelection(
  state: TtqibState,
  selectedObjectIds: readonly string[],
): string[] {
  const ids = new Set<string>();
  for (const objectId of selectedObjectIds) {
    const object = state.objects.find((item) => item.id === objectId);
    if (object) {
      ids.add(object.particleId);
      continue;
    }
    const segMatch = /^obj:seg:([^:]+):/.exec(objectId);
    if (segMatch) ids.add(segMatch[1]);
    const wholeMatch = /^obj:whole:(.+)$/.exec(objectId);
    if (wholeMatch) ids.add(wholeMatch[1]);
    const imageMatch = /^obj:image:(.+)$/.exec(objectId);
    if (imageMatch) ids.add(imageMatch[1]);
  }
  return [...ids];
}

export function isWholeObject(objectId: string, particleId: string): boolean {
  return objectId === wholeObjectId(particleId);
}

export function isImageObject(objectId: string, particleId: string): boolean {
  return objectId === imageObjectId(particleId);
}

export { normalizeTtqibState };
